import { useEffect, useRef, useState } from 'react';
import { FileUp } from 'lucide-react';
import { Modal, Button } from '../ui';
import { uploadMeetingRetro } from '../../services';
import type { MeetingRetro } from '../../data/types';

interface RetroUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  onUploaded: (retro: MeetingRetro) => void;
}

/**
 * Upload a Teams meeting transcript (.docx export primary, .vtt accepted).
 * On success the retro is created in 'scrubbed' state — nothing has been
 * sent to any model yet; the analyst reviews the scrubbed payload first.
 */
export function RetroUploadModal({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  onUploaded,
}: RetroUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setTitle('');
    setMeetingDate(new Date().toISOString().slice(0, 10));
    setError(null);
    setUploading(false);
  }, [isOpen]);

  const handleFileChange = (selected: File | null) => {
    setFile(selected);
    if (selected && !title) {
      // Teams names exports "<meeting>-yyyymmdd_hhmmss-Meeting Recording.docx"
      const base = selected.name.replace(/\.(docx|vtt)$/i, '');
      const match = /^(.+?)-(\d{8})_\d{6}/.exec(base);
      if (match) {
        setTitle(match[1]);
        const d = match[2];
        setMeetingDate(`${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`);
      } else {
        setTitle(base);
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !title.trim() || !meetingDate) {
      setError('Choose a transcript file, title, and meeting date.');
      return;
    }
    setUploading(true);
    setError(null);
    const result = await uploadMeetingRetro(tenantId, file, meetingDate, title.trim());
    setUploading(false);
    if (!result.success) {
      setError(result.error || 'Upload failed');
      return;
    }
    onUploaded(result.data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload meeting transcript"
      description={`${tenantName} — the transcript stays internal; entity indicators are scrubbed before any AI drafting.`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={uploading || !file}>
            {uploading ? 'Parsing & scrubbing…' : 'Upload'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full border-2 border-dashed border-border-default rounded-lg p-6 text-center hover:border-brand-blue transition-colors"
        >
          <FileUp className="w-6 h-6 mx-auto mb-2 text-text-secondary" />
          {file ? (
            <span className="text-sm text-text-primary font-medium">{file.name}</span>
          ) : (
            <span className="text-sm text-text-secondary">
              Click to choose the Teams transcript (.docx or .vtt)
            </span>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,.vtt"
          className="hidden"
          onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
        />

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. July security meeting"
            className="w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-blue"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">
            Meeting date
          </label>
          <input
            type="date"
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-blue"
          />
        </div>

        {error && <p className="text-sm text-accent-red">{error}</p>}
      </div>
    </Modal>
  );
}
